import { ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { useLegacyAreaDialogNavigation } from "@/components/address-selector/composables/useLegacyAreaDialogNavigation";
import { GeneralTypeList } from "@/common/constant";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetPrefByChainDocument,
  GetPrefByChainQuery,
  GetZipCodeByPrefCitySectionBlockDocument,
  GetZipCodeByPrefCitySectionBlockQuery,
  GetChainPaymentMethodDocument,
  GetChainPaymentMethodQuery,
} from "@/generated/graphql";

/**
 * 定期配送登録画面の入力/住所関連アクションを扱う composable。
 */
export const useRegularDeliveryRegisterActions = (ctx: Record<string, any>) => {
  const {
    deliveryIdSelectValues,
    deliverySelectValues,
    deliveryRequestCreateTimeItem,
    regularDeliveryCreateInput,
    errdialog,
    errMessage,
    deliveryRequestCreateDateValues,
    deliveryRequestCreateTimeValues,
    pref_name_befor,
    pref_name,
    prefSelectValue,
    areaAddresSelectClear,
    prefListItems,
    addressDialog,
    zipCodeFromPref,
    originalCityAddress,
    originalSectionAddress,
    deliveryTimeListInput,
    originalBlockName,
    originalAddress,
    queryDeliverytime,
    chkDeliveryId,
    areaClear,
    areaDialog,
    blockList,
    unassignedBlockList,
    btnNonMemberToggle,
    sectionBlockList,
    paymentRequiredFlg,
    wordList,
    areaListItems,
    listDeliveryId,
    GetAreaData,
  } = ctx;

// 配達時間
const delivery_time_input = ref(true);

/**
 * お届け便曜日選択時 @change
 */
const onChangeDeliveryIdSelect = async (): Promise<void> => {
  if (
    deliveryIdSelectValues.value !== null &&
    deliveryIdSelectValues.value !== undefined &&
    deliveryIdSelectValues.value.length > 0
  ) {
    // 全選択
    deliveryIdSelectValues.value.forEach((value) => {
      if (value !== undefined && value !== null && value.value == 99) {
        deliveryIdSelectValues.value = [];
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 1)!
        );
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 2)!
        );
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 3)!
        );
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 4)!
        );
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 5)!
        );
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 6)!
        );
        deliveryIdSelectValues.value!.push(
          GeneralTypeList.week.find((element) => element.value === 7)!
        );
      }
    });

    // 表示用ソート（曜日順）
    deliveryIdSelectValues.value.sort(function (a, b) {
      return a.value - b.value;
    });
  }
};

/**
 * お届け便選択時 @change
 */
const onChangeDeliveryId = async (): Promise<void> => {
  if (deliverySelectValues.value?.delivery_id == 99) {
    // 定期便
    deliveryRequestCreateTimeItem.value = [];

    delivery_time_input.value = true;
    regularDeliveryCreateInput.irregular_delivery_start_time = "";
    regularDeliveryCreateInput.irregular_delivery_end_time = "";
  } else {
    // 定期便以外
    delivery_time_input.value = false;

    regularDeliveryCreateInput.irregular_delivery_start_time = deliverySelectValues.value
      ? deliverySelectValues.value?.delivery_start_time.substring(0, 5)
      : "";
    regularDeliveryCreateInput.irregular_delivery_end_time = deliverySelectValues.value
      ? deliverySelectValues.value?.delivery_end_time.substring(0, 5)
      : "";
  }
  // 配達依頼作成時間作成
  await onChangeDeliveryRequestCreateDate();
};

/**
 * 配達開始時間変更時 @change
 */
const onChangeDeliveryStartTime = async (): Promise<void> => {
  if (
    regularDeliveryCreateInput.irregular_delivery_start_time !== "" &&
    regularDeliveryCreateInput.irregular_delivery_end_time !== "" &&
    regularDeliveryCreateInput.irregular_delivery_start_time! >
      regularDeliveryCreateInput.irregular_delivery_end_time!
  ) {
    errdialog.value = true;
    errMessage.value =
      "配達時間の開始時間は、終了時間以前の時刻を入力してください。";
    regularDeliveryCreateInput.irregular_delivery_start_time = "";
    return;
  }

  // 配達依頼作成時間作成
  await onChangeDeliveryRequestCreateDate();
};

/**
 * 終了開始時間変更時 @change
 */
const onChangeDeliveryEndTime = async (): Promise<void> => {
  if (
    regularDeliveryCreateInput.irregular_delivery_start_time !== "" &&
    regularDeliveryCreateInput.irregular_delivery_end_time !== "" &&
    regularDeliveryCreateInput.irregular_delivery_start_time! >
      regularDeliveryCreateInput.irregular_delivery_end_time!
  ) {
    errdialog.value = true;
    errMessage.value =
      "配達時間の終了時間は、開始時間以降の時刻を入力してください。";
    regularDeliveryCreateInput.irregular_delivery_end_time = "";
    return;
  }

  // 配達依頼作成時間作成
  await onChangeDeliveryRequestCreateDate();
};

/**
 * 配達依頼作成日変更 @change
 */
const onChangeDeliveryRequestCreateDate = async (): Promise<void> => {
  // 配達依頼作成日をセット
  regularDeliveryCreateInput.delivery_request_create_date =
    deliveryRequestCreateDateValues.value
      ? deliveryRequestCreateDateValues.value.delivery_request_create_date
      : 0;

  // 配達依頼作成時間リストを初期化
  deliveryRequestCreateTimeItem.value = [];

  // 配達依頼作成時間の作成
  let endtime = 24;
  if (
    regularDeliveryCreateInput.delivery_request_create_date === 0 &&
    regularDeliveryCreateInput.irregular_delivery_start_time.length > 0
  ) {
    // 当日の場合は配達開始時間まで
    endtime = Number(
      regularDeliveryCreateInput.irregular_delivery_start_time.substring(0, 2)
    );
  }

  for (let i = 0; i < endtime; i++) {
    deliveryRequestCreateTimeItem.value.push(i);
  }

  // 作成時間がリストの最大値を超えていれば 0 にリセット
  if (
    Math.max(...deliveryRequestCreateTimeItem.value) <
    deliveryRequestCreateTimeValues.value!
  ) {
    deliveryRequestCreateTimeValues.value = 0;
  }
};

/**
 * 定期配達有効期間開始日変更時 @change
 */
const formatDateYmd = (value: unknown): string => {
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return value;
  }
  return "";
};

const onChangeValidDateFrom = async (value: unknown): Promise<void> => {
  regularDeliveryCreateInput.valid_date_from = formatDateYmd(value);
  if (
    regularDeliveryCreateInput.valid_date_from !== "" &&
    regularDeliveryCreateInput.valid_date_to !== "" &&
    regularDeliveryCreateInput.valid_date_from! >
      regularDeliveryCreateInput.valid_date_to!
  ) {
    errdialog.value = true;
    errMessage.value =
      "定期配達有効期間の開始日は、終了日以前の日付を入力してください。";
    regularDeliveryCreateInput.valid_date_from = "";
    return;
  }
};

/**
 * 定期配達有効期間終了日変更時 @change
 */
const onChangeValidDateTo = async (value: unknown): Promise<void> => {
  regularDeliveryCreateInput.valid_date_to = formatDateYmd(value);
  if (
    regularDeliveryCreateInput.valid_date_from !== "" &&
    regularDeliveryCreateInput.valid_date_to !== "" &&
    regularDeliveryCreateInput.valid_date_from! >
      regularDeliveryCreateInput.valid_date_to!
  ) {
    errdialog.value = true;
    errMessage.value =
      "定期配達有効期間の終了日は、開始日以降の日付を入力してください。";
    regularDeliveryCreateInput.valid_date_to = "";
    return;
  }
};

/**
 * 住所選択ボタン @Click
 */
const onClickAddressUpdate = async (): Promise<void> => {
  //都道府県データ退避
  pref_name_befor.value = pref_name.value;

  //クリア
  await areaAddresSelectClear();

  //graphql接続
  const apolloClient = await generateApolloClient();

  /**
   * 都道府県データ
   */
  const prefList: ApolloQueryResult<GetPrefByChainQuery> =
    await apolloClient.query<GetPrefByChainQuery>({
      query: GetPrefByChainDocument,
      variables: {
        customer_code: localStorageGetCustomerCode(),
        chain_code: localStorageGetChainCode(),
        shop_code: localStorageGetShopCode(),
      },
      fetchPolicy: "no-cache",
    });
  prefListItems.value = prefList.data.getPrefByChain;
  btnNonMemberToggle.value = 0;
  addressDialog.value = true;
};

const clickMode = ref(false);
/**
 * 丁目のリンクボタン選択時 @click
 */
const onClickAddressBlockLink = async (item: string): Promise<void> => {
  if (clickMode.value == true) {
    return;
  } else {
    clickMode.value = true;
  }

  try {
    const apolloClient = await generateApolloClient();
    let selectedBlock = item;
    if (
      selectedBlock === wordList.lbl_other_block_disp_name ||
      selectedBlock === "その他"
    ) {
      selectedBlock = "";
    }

    // 郵便番号を取得する（master準拠）
    try {
      const getZipCode: ApolloQueryResult<GetZipCodeByPrefCitySectionBlockQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionBlockQuery>({
          query: GetZipCodeByPrefCitySectionBlockDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            city: originalCityAddress.value,
            pref: pref_name.value,
            section: originalSectionAddress.value,
            block: selectedBlock,
          },
          fetchPolicy: "no-cache",
        });
      zipCodeFromPref.value = getZipCode.data.getZipCodeByPrefCitySectionBlock!;
      regularDeliveryCreateInput.delivery_zip_code = zipCodeFromPref.value;
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "該当する郵便番号が見つかりませんでした。";
        throw err;
      } else {
        throw new Error("該当する郵便番号が見つかりませんでした。");
      }
    }

    deliveryTimeListInput.chain_code = localStorageGetChainCode();
    deliveryTimeListInput.shop_code = localStorageGetShopCode();

    originalBlockName.value = selectedBlock ? selectedBlock : originalBlockName.value;

    if (
      originalAddress.value === "" ||
      originalAddress.value == pref_name.value
    ) {
      originalAddress.value =
        pref_name.value +
        originalCityAddress.value +
        originalSectionAddress.value;
    }
    // エリア情報を取得する（master準拠）
    await getAreaList(selectedBlock);

    // 配達時間一覧
    await queryDeliverytime();

    // エリア名取得
    await chkDeliveryId();

    regularDeliveryCreateInput.delivery_address1 =
      pref_name.value +
      originalCityAddress.value +
      originalSectionAddress.value +
      originalBlockName.value;
  } catch (err: unknown) {
    if (err instanceof Error) {
      errMessage.value = err.message;
    } else {
      errMessage.value = "予期しないエラーが発生しました。";
    }

    errdialog.value = true;

    //クリア
    await areaClear();
    return;
  } finally {
    //エリア選択モーダルを閉じる
    areaDialog.value = false;
    blockList.value = [];
    unassignedBlockList.value = [];
    //clickMode.value = false;
  }
};

/**
 * エリア選択画面の戻るボタン @Click
 */
// エリア選択ダイアログの戻る分岐を共通化し、画面固有の状態リセットだけを指定する。
const { onClickAreaDialogReturn } = useLegacyAreaDialogNavigation({
  blockList,
  sectionBlockList,
  originalCityAddress,
  originalSectionAddress,
  unassignedBlockList,
  shouldBackToAddressDialog: () =>
    btnNonMemberToggle.value == 0 &&
    (sectionBlockList.value == null ||
      blockList.value === null ||
      blockList.value === undefined ||
      blockList.value.length == 0),
  onBackToAddressDialog: async () => {
    //郵便番号へ戻る場合
    await areaClear();
    //住所選択モーダルを閉じる
    areaDialog.value = false;
    //郵便番号・都道府県選択をする
    addressDialog.value = true;
  },
  onDefaultExit: () => {
    // 都道府県検索から戻る場合は配送依頼登録画面と同様に未入力状態へ戻す。
    if (btnNonMemberToggle.value === 1) {
      pref_name.value = "";
      prefSelectValue.value = "";
      originalAddress.value = "";
      originalBlockName.value = "";
      regularDeliveryCreateInput.delivery_address1 = "";
      regularDeliveryCreateInput.delivery_zip_code = "";
    } else {
      pref_name.value = pref_name_befor.value;
    }
    //エリア選択モーダルを閉じる
    areaDialog.value = false;
  },
});

/**
 * エリア一覧情報を取得する（master準拠）
 */
const getAreaList = async (item?: string): Promise<void> => {
  let selectedBlock = item;
  if (
    selectedBlock === wordList.lbl_other_block_disp_name ||
    selectedBlock === "その他"
  ) {
    selectedBlock = "";
  }

  originalBlockName.value = selectedBlock ? selectedBlock : originalBlockName.value;
  if (originalAddress.value === "" || originalAddress.value == pref_name.value) {
    originalAddress.value =
      pref_name.value + originalCityAddress.value + originalSectionAddress.value;
  }

  // 既存データをクリアして再取得
  areaListItems.value = [];
  deliveryTimeListInput.area_code = [];
  listDeliveryId.value = [];

  await GetAreaData(
    originalAddress.value + originalBlockName.value,
    regularDeliveryCreateInput.delivery_zip_code
  );
  regularDeliveryCreateInput.delivery_address1 =
    originalAddress.value + originalBlockName.value;
};

/**
 * 決済方法によって支払金額必須か判定 @change
 */
const onChangePaymentMethod = async (item: string): Promise<void> => {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    const paymentList: ApolloQueryResult<GetChainPaymentMethodQuery> =
      await apolloClient.query<GetChainPaymentMethodQuery>({
        query: GetChainPaymentMethodDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
        },
        fetchPolicy: "no-cache",
      });
    const _paymentList = paymentList.data.getChainPaymentMethod;
    paymentRequiredFlg.value = false;
    if (_paymentList!.length > 0) {
      for (let i = 0; i < _paymentList!.length; i++) {
        if (
          _paymentList![i]!.payment_method! === item &&
          _paymentList![i]!.payment_amount_required! === "1"
        ) {
          paymentRequiredFlg.value = true;
        }
      }
    }
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }

    errdialog.value = true;
    errMessage.value = "支払方法の取得に失敗しました。";
    return;
  }
};

  return {
    delivery_time_input,
    onChangeDeliveryIdSelect,
    onChangeDeliveryId,
    onChangeDeliveryStartTime,
    onChangeDeliveryEndTime,
    onChangeDeliveryRequestCreateDate,
    formatDateYmd,
    onChangeValidDateFrom,
    onChangeValidDateTo,
    onClickAddressUpdate,
    clickMode,
    onClickAddressBlockLink,
    onClickAreaDialogReturn,
    getAreaList,
    onChangePaymentMethod,
  };
};
